import { Component, Input, ViewChild, OnInit, EventEmitter, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, Column, GridOptions, GridReadyEvent, RowNode } from 'ag-grid-community';
import { CommonService } from '../../../services/common.service';
import { ReportsService, ReportMeta } from '../../../services/reports.service';

import {
  customAgGridComponents,
  ICustomAgGridComponents,
} from 'src/app/model/ag-grid-custom-models/custom-components-config';
import { ImportDetailDialogComponent } from '../dialogs/import-detail-dialog.component';
import { EventPayload } from '../driver-import-interfaces';
import { DriverImportColumns } from 'src/app/columns/driverImport.columns';

@Component({
  selector: 'vrm-driver-import-grid',
  templateUrl: './vrm-driver-import-grid.component.html',
  styleUrls: ['./vrm-driver-import-grid.component.scss'],
})
export class VrmDriverImportGridComponent implements OnInit {
  @Input() configurationDataAvailable: boolean;
  @Input() metadata: ReportMeta;
  @Input() rawData: Array<any> = [];
  @Output() editNewConfig: EventEmitter<EventPayload> = new EventEmitter();
  @Output() uploadDrivers: EventEmitter<EventPayload> = new EventEmitter();
  @Output() dataChange: EventEmitter<EventPayload> = new EventEmitter();

  columnsToDisplay: ColDef[] = this.driverImportColumns.columnDefinitions;
  agGridDefaultColumnConfig: ColDef;
  initialLoad: boolean = true;
  initialView: string;
  customComponents: ICustomAgGridComponents = customAgGridComponents;
  gridOptions: GridOptions;

  @ViewChild('agGrid', { static: true }) agGrid: AgGridAngular;
  translation: TranslateService;

  constructor(
    private dialog: MatDialog,
    public cService: CommonService,
    public rptService: ReportsService,
    private translate: TranslateService,
    public driverImportColumns: DriverImportColumns
  ) {
    this.initialView = cService.isPcViewport ? 'pc' : 'mobile';
  }

  ngOnInit() {
    this.initializeAgGridProperties();
  }

  initializeAgGridProperties() {
    this.gridOptions = {
      pagination: true,
      paginationPageSize: 50,
      rowHeight: 40,
    };
  }

  agGridReady(event: GridReadyEvent) {
    this.displayColumnsByViewportSize();
    this.initialLoad = false;
  }

  onViewDetail(data: RowNode) {
    const detailDialogRef = this.dialog.open(ImportDetailDialogComponent, {
      width: '70%',
      data: { details: data },
    });
    detailDialogRef.afterClosed().subscribe(() => this.updateGrid());
  }

  updateGrid() {
    this.dataChange.emit({ command: 'update' });
  }

  setQuickFilter(searchText: string) {
    this.agGrid.api.setQuickFilter(searchText);
  }

  onUploadDrivers() {
    this.uploadDrivers.emit({ command: 'upload' });
  }

  onEditConfiguration() {
    this.editNewConfig.emit({ command: 'edit' });
  }

  displayColumnsByViewportSize() {
    if (this.cService.isMobileViewport) {
      this.agGrid.api.closeToolPanel();
    }

    this.translate.get(['DRIVER_IMPORT']).subscribe((translation) => {
      this.getTranslatedColumns(translation['DRIVER_IMPORT']);
      this.setupActionField();
      this.restoreStateAcrossGrids();
    });

    if (!this.rawData) {
      this.rawData = [];
    }
  }

  setupActionField() {
    const actionField = this.columnsToDisplay.find((a) => a.field === 'actions');
    if (actionField) {
      actionField.cellRendererParams = {
        onViewDetail: (rowData: RowNode) => this.onViewDetail(rowData),
      };
    }
    if (!this.initialLoad) {
      this.agGrid.columnDefs = this.columnsToDisplay;
    }
  }

  getTranslatedColumns(translations: { [key: string]: string }) {
    this.columnsToDisplay.forEach((col) => (col.headerName = translations[col.colId] || col.headerName));
  }

  restoreStateAcrossGrids() {
    if (this.metadata?.structure) {
      const columnKeys = Object.keys(this.metadata.structure);
      this.agGrid.columnApi.moveColumns(columnKeys, 0);
    }

    if (this.rptService.gridState.filterModel) {
      this.agGrid.api.setFilterModel(this.rptService.gridState.filterModel);
    }

    this.attemptToFitGridColumns();
  }

  attemptToFitGridColumns() {
    const visibleColumns = this.columnsToDisplay.filter((col) => !col.hide);
    visibleColumns.forEach((columnDef: ColDef) => {
      if (!columnDef.width) {
        this.agGrid.columnApi.autoSizeColumn(columnDef.colId);
      }
    });

    this.gridColumnResized();
  }

  gridColumnResized() {
    this.agGrid.api.setHeaderHeight(this.rptService.agGridConfig.common.headerHeight);
    const columns = this.agGrid.columnApi.getColumns();

    columns.forEach((column) => {
      const columnHeaderName = column.getColDef().headerName;
      if (columnHeaderName) {
        this.adjustColumnHeaderHeight(column, columnHeaderName);
      }
    });
  }

  adjustColumnHeaderHeight(column: Column, columnHeaderName: string) {
    const columnHeaderWords = columnHeaderName.split(' ');
    const hasOverTwoWords = columnHeaderWords.length > 2;
    const columnActualWidth = column.getActualWidth();
    const pixelPerChar = 7.1;
    const langCode = this.cService.userProfile?.languageCode?.toLowerCase() || '';

    let colHeaderHeight = 50;

    if (hasOverTwoWords) {
      const firstWordPair = columnHeaderWords[0] + ' ' + columnHeaderWords[1];
      const remainingWords = columnHeaderWords.slice(2).join(' ');

      const maxCharPossible = (columnActualWidth - 20) / pixelPerChar;
      if (remainingWords.length > maxCharPossible) {
        colHeaderHeight = 85;
      } else if (firstWordPair.length > maxCharPossible) {
        colHeaderHeight = 65;
      } else if (firstWordPair.length >= maxCharPossible - 2.5) {
        colHeaderHeight = 60;
      }
    } else if (['jp', 'kr', 'ko'].includes(langCode)) {
      colHeaderHeight = 60;
    }

    this.agGrid.api.setHeaderHeight(colHeaderHeight);
  }

  getRowHeight(defaultHeight: number) {
    return (params) => {
      let rowHeight = defaultHeight;
      if (params.data && params.dataToResize) {
        params.dataToResize.forEach((element) => {
          if (params.data[element] && params.data[element].length * defaultHeight > rowHeight) {
            rowHeight = params.data[element].length * defaultHeight;
          }
        });
      }
      return rowHeight;
    };
  }
}
